import { type ChatThreadKind } from '../interface/support-chat';

// How the client names a conversation BEFORE the server has.
//
// The server's thread key (`support:{party}:{agent}`, `peer:{party}:{election}:{parent}:{child}`)
// is only known once `POST threads` succeeds, and the outbox exists precisely to hold a message
// before that. So every piece of client state is keyed on a local ref the client can build
// from what it already knows, and the server key is stored beside it once it arrives.
//
// A future per-entity room is a third `kind` here and nowhere else.

export type ChatThreadRef =
	| { kind: 'support' }
	| {
			kind: 'peer';
			/** The other agent's core user id — the same integer the support key uses. */
			counterpartCoreUserId: number;
			electionId: number;
	  };

export const SUPPORT_THREAD_REF: ChatThreadRef = { kind: 'support' };

/** The local id that keys caches, the outbox and the thread store. */
export const localThreadId = (ref: ChatThreadRef): string =>
	ref.kind === 'support' ? 'support' : `peer:${ref.electionId}:${ref.counterpartCoreUserId}`;

/** Inverse of {@link localThreadId}. Null for anything it did not produce. */
export const parseLocalThreadId = (id: string): ChatThreadRef | null => {
	if (id === 'support') return SUPPORT_THREAD_REF;

	const match = /^peer:(\d+):(\d+)$/.exec(id);
	if (!match) return null;

	const electionId = Number(match[1]);
	const counterpartCoreUserId = Number(match[2]);
	if (electionId <= 0 || counterpartCoreUserId <= 0) return null;

	return { kind: 'peer', counterpartCoreUserId, electionId };
};

export const kindOf = (ref: ChatThreadRef): ChatThreadKind => ref.kind;

/** Whether a SERVER key names an agent↔agent thread. */
export const isPeerKey = (threadKey: string | null | undefined): boolean => !!threadKey && threadKey.startsWith('peer:');
